import { Client, Company, isClient, isCompany } from 'src/constants';
import { ContextBarTarget } from 'src/context/contextBarContext';
import { useAppSelector } from 'src/hooks/useStore';
import {
  selectAllCompaniesMap,
  selectClientsMap,
} from 'src/store/clients/types';

type EntityType = Client | Company;

interface ChannelTargetData {
  membershipType?: string;
  companyID?: string;
  singleClientId?: string;
}

/**
 * Extracts the context bar target (entityType and entityId) from channel data.
 * Parent components should normalize their channel data before calling this.
 */
export const getContextBarTarget = (
  channel: ChannelTargetData | undefined,
): ContextBarTarget => {
  let entityType: 'client' | 'company' = 'client';
  let entityId = '';

  if (
    channel?.membershipType === 'company' ||
    channel?.membershipType === 'group'
  ) {
    entityType = 'company';
    entityId = channel.companyID ?? '';
  } else if (channel?.membershipType === 'individual') {
    entityType = 'client';
    entityId = channel.singleClientId ?? '';
  }

  return { id: entityId, entityType };
};

export const useEntity = (
  entityType: 'client' | 'company' | undefined,
  entityId: string | undefined,
): EntityType | undefined => {
  const companies = useAppSelector(selectAllCompaniesMap);
  const clients = useAppSelector(selectClientsMap);

  if (!entityType || !entityId) return undefined;

  if (entityType === 'client') {
    return clients.get(entityId);
  }
  return companies.get(entityId);
};

export const getEntityName = (entity: EntityType | undefined) => {
  if (isClient(entity))
    return `${entity.fields.givenName ?? ''} ${entity.fields.familyName ?? ''}`.trim();
  if (isCompany(entity)) return entity.fields.name;
  return '';
};
